import { gsap } from "gsap";
import Splide from '@splidejs/splide';

export default class Masterplan {
    constructor() {
        this.isAnimating = false;
        this.init();
    }

    init() {
        this.cacheDOM();
        if (!this.dom.view) return;

        this.createMasterTimeline();
        this.bindEvents();
        this.linksSlider();
    }

    cacheDOM() {
        this.dom = {
            wrapper: document.querySelector('.masterplan__container'),
            inside: document.querySelector('.masterplan'),
            view: document.querySelector('.masterplan__view'),
            links: document.querySelector('.masterplan__links'),
            slider: document.querySelector('.masterplan__links .splide'),
            visit: document.querySelector('.masterplan__visit'),
            viewFooter: document.querySelector('.masterplan__view-footer'),
            viewHeader: document.querySelector('.masterplan__view-header'),
            viewDesc: document.querySelector('.masterplan__view-description'),
            viewPlans: document.querySelector('.masterplan__view-plans'),
            toggleBtn: document.querySelector('.masterplan__view-btn--toggle'),
            closeBtn: document.querySelector('.masterplan__view-btn--close'),
            mainToggle: document.querySelector('.masterplan__toggle'),
            mainToggleBtn: document.querySelector('.masterplan__toggle-btn'),
            mainToggleSpan: document.querySelector('.masterplan__toggle-btn span'),
            showTab: document.querySelector('.masterplan__show-tab'),
            hotspots: document.querySelectorAll('.banner-hotspots__item'),
            plans: document.querySelectorAll('.plan'),
        };

        if (this.dom.showTab) {
            gsap.set(this.dom.showTab, {
                xPercent: 120,
                autoAlpha: 0,
                pointerEvents: 'none'
            });
        }
    }

    createMasterTimeline() {
        this.masterTL = gsap.timeline({ paused: true, reversed: true });

        if (window.matchMedia("(min-width: 1024px)").matches) {
            this.masterTL
                .addLabel("start")
                // 1.1 Hide footer
                .to(this.dom.viewFooter, {
                    autoAlpha: 0,
                    height: 0,
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")
                // 1.2 Hide main toggle button
                .to(this.dom.mainToggle, {
                    autoAlpha: 0,
                    duration: 0.4,
                    ease: "power3.inOut"
                }, "start")
                // 1.3 Hide links and visit instantly
                .set([this.dom.links, this.dom.visit], {
                    display: "none",
                    autoAlpha: 0,
                    duration: 0.3,
                }, "start")
                // 1.4 Expand width, height and position
                .to(this.dom.view, {
                    width: "20vw",
                    height: "85vh",
                    padding: "0 15px 20px",
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")
                .to(this.dom.wrapper, {
                    // right: "40px",
                    bottom: "0",
                    // top: "50%",
                    // y: "-50%",
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")

                // 1.5 Reveal header, description, plans
                .to([this.dom.viewDesc, this.dom.viewPlans], {
                    display: "block",
                    autoAlpha: 1,
                    duration: 0.4,
                    stagger: 0.05,
                    ease: "power2.out"
                }, "-=0.3")
                .to(this.dom.viewHeader, {
                    display: "flex",
                    autoAlpha: 1,
                    duration: 0.4,
                    ease: "power2.out"
                }, "<");
        } else {
            this.masterTL
                .addLabel("start")
                // 1.1 Hide footer
                .to(this.dom.viewFooter, {
                    autoAlpha: 0,
                    height: 0,
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")
                // 1.2 Hide main toggle button
                .to(this.dom.mainToggle, {
                    autoAlpha: 0,
                    duration: 0.4,
                    ease: "power3.inOut"
                }, "start")
                // 1.3 Hide links and visit instantly
                .set([this.dom.links, this.dom.visit], {
                    display: "none",
                    autoAlpha: 0,
                    duration: 0.3,
                }, "start")
                .to(this.dom.inside, {
                    height: "85vh",
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")
                // 1.4 Expand width, height and position
                .to(this.dom.view, {
                    width: "calc(100vw - 32px)",
                    height: "85vh",
                    padding: "20px 15px",
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")
                .to(this.dom.wrapper, {
                    // right: "40px",
                    bottom: "0",
                    // top: "50%",
                    // y: "-50%",
                    duration: 0.6,
                    ease: "power3.inOut"
                }, "start")

                // 1.5 Reveal header, description, plans
                .to([this.dom.viewDesc, this.dom.viewPlans], {
                    display: "block",
                    autoAlpha: 1,
                    duration: 0.4,
                    stagger: 0.05,
                    ease: "power2.out"
                }, "-=0.3")
                .to(this.dom.viewHeader, {
                    display: "flex",
                    autoAlpha: 1,
                    duration: 0.4,
                    ease: "power2.out"
                }, "<");
        }
    }

    bindEvents() {
        // 1. Main widget toggle button
        if (this.dom.mainToggleBtn) {
            this.dom.mainToggleBtn.addEventListener('click', () => this.toggleWidget());
        }

        if (this.dom.showTab) {
            this.dom.showTab.addEventListener('click', () => this.toggleWidget());
        }

        // 2. Toggle button click
        if (this.dom.toggleBtn) {
            this.dom.toggleBtn.addEventListener('click', () => this.toggleView());
        }

        // 3. Close button click
        if (this.dom.closeBtn) {
            this.dom.closeBtn.addEventListener('click', () => this.closeView());
        }

        // 4. Plan open buttons
        this.dom.plans.forEach(plan => {
            const openBtn = plan.querySelector('.masterplan__view-btn--open-plan');
            if (openBtn) {
                openBtn.addEventListener('click', () => this.togglePlan(plan));
            }
        });

        // 5. Hotspot buttons
        this.dom.hotspots.forEach(hotspot => {
            hotspot.addEventListener('click', (e) => this.handleHotspotClick(e));
        });
    }

    toggleWidget() {
        if (this.isAnimating) return;

        if (!this.dom.showTab) {
            return;
        }

        this.isAnimating = true;
        const isCollapsed = this.dom.wrapper.classList.contains('is-collapsed');
        const widgetElements = [this.dom.mainToggle, this.dom.inside];

        if (isCollapsed) {
            this.openWidget(widgetElements);
        } else {
            this.closeWidget(widgetElements);
        }
    }

    closeWidget(widgetElements) {
        this.closeView();

        const tl = gsap.timeline({
            onComplete: () => {
                this.dom.wrapper.classList.add('is-collapsed');
                this.isAnimating = false;
            }
        });

        tl.to(widgetElements, {
            xPercent: 120,
            autoAlpha: 0,
            duration: 0.45,
            ease: 'power3.inOut'
        })
            .set(this.dom.showTab, { pointerEvents: 'auto', visibility: 'visible' })
            .to(this.dom.showTab, {
                xPercent: 0,
                autoAlpha: 1,
                duration: 0.35,
                ease: 'power3.out'
            }, '-=0.12');
    }

    openWidget(widgetElements) {
        const tl = gsap.timeline({
            onComplete: () => {
                this.dom.wrapper.classList.remove('is-collapsed');
                this.isAnimating = false;
            }
        });

        tl.to(this.dom.showTab, {
            xPercent: 120,
            autoAlpha: 0,
            duration: 0.3,
            ease: 'power3.in'
        })
            .set(this.dom.showTab, { pointerEvents: 'none', visibility: 'hidden' })
            .to(widgetElements, {
                xPercent: 0,
                autoAlpha: 1,
                duration: 0.45,
                ease: 'power3.out'
            });
    }

    toggleView() {
        this.masterTL.play();
    }

    closeView() {
        this.masterTL.reverse();
        // Also close any open plans when closing the main view
        this.closeAllPlans();
    }

    togglePlan(activePlan) {
        const footer = activePlan.querySelector('.plan__footer');
        const btn = activePlan.querySelector('.masterplan__view-btn--open-plan');

        // Check if this plan is already open
        const isOpen = activePlan.classList.contains('is-open');

        // Close all other plans first
        this.closeAllPlans();

        if (!isOpen) {
            // Open this plan
            activePlan.classList.add('is-open');

            gsap.to(".plan__title", {
                fontSize: "24px",
                duration: 0.4,
                ease: "power2.out"
            });

            gsap.to(footer, {
                height: "auto",
                autoAlpha: 1,
                duration: 0.4,
                ease: "power2.out"
            });

            gsap.to(btn, {
                autoAlpha: 0,
                duration: 0.2
            });
        }
    }

    closeAllPlans() {
        this.dom.plans.forEach(plan => {
            if (plan.classList.contains('is-open')) {
                const footer = plan.querySelector('.plan__footer');
                const btn = plan.querySelector('.masterplan__view-btn--open-plan');

                plan.classList.remove('is-open');

                gsap.to(footer, {
                    height: 0,
                    autoAlpha: 0,
                    duration: 0.3,
                    ease: "power2.in"
                });

                gsap.to(btn, {
                    autoAlpha: 1,
                    duration: 0.2
                });
            }
        });
    }

    handleHotspotClick(e) {
        const btn = e.currentTarget;
        const planId = btn.dataset.plan;

        // If view is closed, open it first
        if (this.masterTL.reversed()) {
            this.masterTL.play().then(() => {
                this.activatePlanById(planId);
            });
        } else {
            this.activatePlanById(planId);
        }
    }

    activatePlanById(planId) {
        if (!planId) return;

        const targetPlan = document.getElementById(planId);
        // console.log(targetPlan);
        if (targetPlan) {
            this.togglePlan(targetPlan);
            // Scroll within the view container instead of the whole page
            const viewContainer = this.dom.view;
            const targetOffset = targetPlan.offsetTop;
            gsap.to(viewContainer, {
                scrollTop: targetOffset,
                duration: 0.6,
                ease: 'power2.inOut'
            });
        }
    }

    linksSlider() {
        console.log(this.dom.slider);

        const splide = new Splide(this.dom.slider, {
            type: 'fade',
            pagination: false,
            arrows: false,
            drag: true,
            perPage: 1,
            autoplay: true,
            interval: 3000,
            pauseOnHover: false,
            pauseOnFocus: false,
            rewind: true,
        });

        splide.mount();
    }
}